import { useState, useRef, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { MicButton } from "@/components/MicButton";
import { Container, PageHeader, GenerateButton } from "@/components/shared";
import { toast } from "sonner";
import { Send, Award, RotateCcw } from "lucide-react";

const PROFILES = ["Troll", "Journaliste", "Professeur", "Personne sceptique", "Contradicteur de mauvaise foi", "Personne curieuse"];
const CRITERIA = [
  { key: "clarity", label: "Clarté" },
  { key: "logic", label: "Logique" },
  { key: "respect", label: "Respect" },
  { key: "persuasion", label: "Conviction" },
];

export default function Debate() {
  const [profile, setProfile] = useState(null);
  const [debateId, setDebateId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [score, setScore] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async () => {
    if (!input.trim()) return;
    const text = input;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/debate", { debate_id: debateId, profile, message: text });
      setDebateId(data.debate_id);
      setMessages((m) => [...m, { role: "ai", text: data.reply }]);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const evaluate = async () => {
    if (!debateId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/debate/score", { debate_id: debateId });
      setScore(data);
      if (data.new_badges?.length) toast.success(`Badge débloqué : ${data.new_badges.join(", ")}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const reset = () => { setProfile(null); setDebateId(null); setMessages([]); setScore(null); };

  if (!profile) {
    return (
      <Container>
        <PageHeader eyebrow="Entraînement" title="Entraînement au débat"
          subtitle="Choisissez un adversaire. L'IA débat en temps réel, puis note votre performance." />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PROFILES.map((p, i) => (
            <button key={p} data-testid={`profile-${p}`} onClick={() => setProfile(p)}
              className="card-surface noise p-6 text-left fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <div className="w-10 h-10 rounded-xl bg-gold/15 flex items-center justify-center mb-4">
                <span className="text-gold font-display text-lg">{p[0]}</span>
              </div>
              <h3 className="font-display text-lg">{p}</h3>
            </button>
          ))}
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-gold text-xs uppercase tracking-[0.2em]">Adversaire</p>
          <h1 className="font-display text-2xl">{profile}</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="debate-reset" onClick={reset}
            className="flex items-center gap-2 text-sm text-white/50 hover:text-white border border-white/10 rounded-full px-4 py-2">
            <RotateCcw size={15} /> Changer
          </button>
          {messages.length >= 2 && (
            <GenerateButton data-testid="debate-score-button" onClick={evaluate} disabled={busy}>
              <Award size={16} /> Me noter
            </GenerateButton>
          )}
        </div>
      </div>

      <div className="card-surface p-4 md:p-5 mb-4 min-h-[320px] flex flex-col">
        <div className="flex-1 space-y-3 overflow-y-auto max-h-[50vh]">
          {messages.length === 0 && <p className="text-white/30 text-sm text-center py-12">Lancez le débat en écrivant votre premier argument.</p>}
          {messages.map((m, i) => (
            <div key={i} data-testid={`debate-msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.role === "user" ? "bg-white/10 text-white" : "bg-[#111] border border-white/5 text-white/85"}`}>
                {m.text}
              </div>
            </div>
          ))}
          {busy && !score && <div className="text-white/30 text-sm">L'adversaire réfléchit...</div>}
          <div ref={endRef} />
        </div>
        <div className="flex items-center gap-2 mt-4 border-t border-white/10 pt-4">
          <input data-testid="debate-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Votre argument..."
            className="flex-1 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2.5 outline-none text-sm" />
          <MicButton testid="debate-input-mic" value={input} onChange={setInput} />
          <button data-testid="debate-send" onClick={send} disabled={busy || !input.trim()}
            className="w-10 h-10 rounded-full gold-gradient flex items-center justify-center text-black disabled:opacity-40">
            <Send size={16} />
          </button>
        </div>
      </div>

      {score && (
        <div data-testid="debate-score-result" className="card-surface p-6 fade-up">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-xl">Votre note</h3>
            <div className="text-3xl font-display gold-text">{score.overall}/100</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            {CRITERIA.map((c) => (
              <div key={c.key}>
                <div className="flex justify-between text-sm mb-1"><span className="text-white/60">{c.label}</span><span className="text-gold">{score[c.key]}</span></div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full gold-gradient" style={{ width: `${score[c.key]}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <p className="text-gold text-xs uppercase tracking-[0.15em]">Comment progresser</p>
            {(score.advice || []).map((a, i) => (
              <p key={i} className="text-white/80 text-sm flex gap-2"><span className="text-gold/60">—</span> {a}</p>
            ))}
          </div>
        </div>
      )}
    </Container>
  );
}
